import axios from 'axios'
import firebase from 'firebase/app'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  ProfileData,
  UpdateAvatarResponse,
  UpdatePasswordPayload,
  UpdateProfilePayload,
} from 'api/arcMosaico/types'
import { api } from 'helpers'
import type { FirebaseUser, FirebaseTokenManager } from '../../TeacherSignup/service/types'

function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

export async function fetchProfile(): Promise<ProfileData> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.profile.show)
  assertAuthenticated(response)
  return response.data as ProfileData
}

export async function updateProfile(data: UpdateProfilePayload): Promise<void> {
  const response = await api.patch(ARC_MOSAICO_ENDPOINTS.profile.update, data)
  assertAuthenticated(response)
}

export async function uploadAvatar(base64: string): Promise<UpdateAvatarResponse> {
  const response = await api.patch(ARC_MOSAICO_ENDPOINTS.profile.updateAvatar, { avatar: base64 })
  assertAuthenticated(response)
  return response.data as UpdateAvatarResponse
}

export async function updatePassword(data: UpdatePasswordPayload): Promise<void> {
  const response = await api.patch(ARC_MOSAICO_ENDPOINTS.profile.updatePassword, data)
  assertAuthenticated(response)
}

/**
 * Connects the user's Google account to their profile.
 * Uses axios directly because backend expects camelCase params (firebaseUser),
 * but the api helper converts to snake_case.
 */
export async function connectGoogle(
  firebaseUser: FirebaseUser,
  tokenManager?: FirebaseTokenManager,
): Promise<void> {
  const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')

  const payload: Record<string, unknown> = {
    firebaseUser: {
      email: firebaseUser.email,
      uid: firebaseUser.uid,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
    },
  }

  if (tokenManager) {
    payload.session = {
      firebaseUser: {
        stsTokenManager: {
          expirationTime: tokenManager.expirationTime,
          accessToken: tokenManager.accessToken,
          refreshToken: tokenManager.refreshToken,
        },
      },
    }
  }

  await axios.post(ARC_MOSAICO_ENDPOINTS.profile.connectGoogle, payload, {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token || '',
    },
  })
}

/** Disconnects the user's Google account, reverting to email-based auth */
export async function disconnectGoogle(): Promise<void> {
  const response = await api.delete(ARC_MOSAICO_ENDPOINTS.profile.disconnectGoogle)
  assertAuthenticated(response)

  // Clear Firebase session so the user cannot login with Google until reconnected
  try {
    await firebase.auth().signOut()
  } catch {
    // Firebase may not be initialized — safe to ignore
  }
}
